/**
 * Diacritic-insensitive text folding for the site search.
 *
 * A driver looking for Łódź types `lodz`, and one typing on a phone keyboard
 * without Polish letters has no other option. So both the haystack and the
 * needle are folded to bare ASCII before comparison.
 *
 * The rule matches `normalizeOperatorName` in `@ev/shared`, minus the legal-form
 * stripping that only makes sense for company names: NFD decomposition removes
 * the combining marks behind ą ć ę ń ó ś ź ż, and the handful of letters Unicode
 * does *not* decompose are mapped by hand.
 */

/** Letters NFD leaves alone, because the stroke is part of the glyph. */
const NON_DECOMPOSING_DIACRITICS: Readonly<Record<string, string>> = {
  ł: 'l',
  Ł: 'L',
  đ: 'd',
  ø: 'o',
  ß: 'ss',
}

const COMBINING_MARKS = /[̀-ͯ]/g

/**
 * Fold a string to its comparison key: no diacritics, lower case, punctuation
 * collapsed to single spaces.
 *
 * `"Łódź"` and `"lodz"` both fold to `"lodz"`; `"Bielsko-Biała"` folds to
 * `"bielsko biala"`, so either half matches on its own.
 */
export const foldText = (value: string): string =>
  value
    .replace(/[łŁđøß]/g, (char) => NON_DECOMPOSING_DIACRITICS[char] ?? char)
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Does a folded haystack contain every folded word of the query?
 *
 * Word-wise rather than substring, so `paliw lodz` finds "Stacja paliw Łódź"
 * even though the words are not adjacent in that order. An empty query matches
 * everything - it is not a filter until somebody types something.
 */
export const matchesFoldedQuery = (haystack: string, foldedQuery: string): boolean => {
  if (foldedQuery === '') return true
  return foldedQuery.split(' ').every((word) => haystack.includes(word))
}

/** The query, folded and split into the words that must all match. */
export const searchTokens = (value: string): string[] =>
  foldText(value)
    .split(' ')
    .filter((token) => token !== '')

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

/**
 * Deciding which matches to show first.
 *
 * Membership is not enough once a query matches more sites than fit on screen.
 * Typing `gda` matches Gdańsk and it matches "Stacja przy ul. Gdańskiej 12" in
 * Warsaw, and a list that shows them in whatever order the artifact happened to
 * store them is a list the visitor has to read all of.
 */

/** Anything the ranking runs against: three fields, already folded. */
export interface SearchableFields {
  readonly name: string
  readonly city: string
  /**
   * The operator's display name.
   *
   * Searched because the filter predicate searches it: typing "Tauron" narrows
   * the map, and a result list that ignored the operator would answer that with
   * "nothing found" for a query the map had just acted on.
   */
  readonly operator: string
}

/** Lower is better. A hit at the start of a field beats one inside a word. */
const FIELD_PREFIX = 0
const WORD_PREFIX = 1
const SUBSTRING = 2
const NO_MATCH = Number.POSITIVE_INFINITY

export const tokenScore = (haystack: string, token: string): number => {
  if (haystack.startsWith(token)) return FIELD_PREFIX
  const at = haystack.indexOf(token)
  if (at < 0) return NO_MATCH
  // A space before the hit means the token starts a word rather than ending one.
  return haystack[at - 1] === ' ' ? WORD_PREFIX : SUBSTRING
}

/**
 * The name is worth slightly more than the city, and the city more than the
 * operator: searching a city lists the site actually called that before the
 * dozen merely in it, and a brand lists whatever is named after the brand
 * before the several hundred sites that merely belong to it.
 */
const CITY_PENALTY = 0.5
const OPERATOR_PENALTY = 1.5

/**
 * Score one site against every token, or `null` if any token is unmatched.
 * Tokens are ANDed, so `gdansk galeria` narrows rather than widens.
 */
export const scoreMatch = (fields: SearchableFields, tokens: readonly string[]): number | null => {
  if (tokens.length === 0) return null

  let total = 0
  let matchedAll = true

  tokens.forEach((token) => {
    const best = Math.min(
      tokenScore(fields.name, token),
      tokenScore(fields.city, token) + CITY_PENALTY,
      tokenScore(fields.operator, token) + OPERATOR_PENALTY,
    )
    if (best === NO_MATCH) matchedAll = false
    else total += best
  })

  return matchedAll ? total : null
}
